import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {connect} from "react-redux";
import ContactItem from "src/app/contact/contact-main/contact-list/contact-item/ContactItem";
import { useTranslation } from 'react-i18next';
import TagSearchingInput from "src/app/shared/components/tag-searching-input/TagSearchingInput";
import { ISearchingTag } from "src/app/shared/components/tag-searching-input/~types/models/SearchingTag";
import { getContactSearchingTags } from "src/app/contact/~store/actions/constants";
import { REQUESTED_NUMBER_OF_CONTACTS } from "src/app/contact/contact-main/contact-list/ContactListConstants";
import { TRIGGER_SCROLL_HEIGHT } from "src/app/chats/chats-main/chats-list/ChatsListConstants";
import { Table } from "reactstrap";
import { IApplicationState } from "src/~store/models/ApplicationState";
import { contactsSelector, countOfContactsSelector, isChatVisibleSelector, isReceivingContactsSelector } from "src/app/contact/~store/selector";
import { IMailingRecipientsContactListProps } from "./~types/MailingRecipientsContactListProps";
import Loader from "src/app/shared/components/loader/Loader";
import { getContactsByTagsForMailing, getContactsByTagsForMailingWithoutUpdateTags } from "src/app/mailing/~store/actions/actions";
import { mailingItemInfoSelector } from "src/app/mailing/~store/selector";

type ReduxType = ReturnType<typeof mapStateToProps> & IMailingRecipientsContactListProps;

const MailingRecipientsContactList = (props: ReduxType) => {
    const { t } = useTranslation();
    
    const contactsListRef = useRef<HTMLDivElement>(null);
    const prevContactsLength = useRef<number>(0);
    const wasReceiving = useRef<boolean>(false); 
    const [isAllLoadedLocally, setIsAllLoadedLocally] = useState<boolean>(false);

    const resetPaginationState = () => {
        setIsAllLoadedLocally(false);
        prevContactsLength.current = 0;
        wasReceiving.current = true;
    };

    useEffect(() => {
        resetPaginationState();
        props.getContactsByTagsForMailing(0, REQUESTED_NUMBER_OF_CONTACTS, props.mailingItemInfo.searchingTagsForContacts);
    }, [props.mailingItemInfo.mailingId]);

    const _onSearchClicked = (tags: ISearchingTag[]) => {
        resetPaginationState();
        props.getContactsByTagsForMailing(0, REQUESTED_NUMBER_OF_CONTACTS, tags);
    };

    useEffect(() => {
        if (wasReceiving.current && !props.isReceivingContacts) {
            const newLoadedCount = props.contacts.length - prevContactsLength.current;

            if (newLoadedCount >= 0 && newLoadedCount < REQUESTED_NUMBER_OF_CONTACTS) {
                setIsAllLoadedLocally(true);
            }
            prevContactsLength.current = props.contacts.length;
        }
        wasReceiving.current = props.isReceivingContacts;
    }, [props.isReceivingContacts, props.contacts.length]);


    useLayoutEffect(() => {
        if (!contactsListRef.current) return;
        if (!(contactsListRef.current.scrollHeight > contactsListRef.current.clientHeight) &&
            !props.isReceivingContacts && !isAllLoadedLocally && props.contacts.length > 0)
        {
            props.getContactsByTagsForMailingWithoutUpdateTags(props.countOfContacts, REQUESTED_NUMBER_OF_CONTACTS, props.mailingItemInfo.searchingTagsForContacts);
        }
    }, [props.contacts.length, props.isReceivingContacts, isAllLoadedLocally]);

    const _onScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        if (props.isReceivingContacts || isAllLoadedLocally) return;

        if ((e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight) <= TRIGGER_SCROLL_HEIGHT)
        {
            props.getContactsByTagsForMailingWithoutUpdateTags(props.countOfContacts, REQUESTED_NUMBER_OF_CONTACTS, props.mailingItemInfo.searchingTagsForContacts);
        }
    }

    const {isReceivingContacts} = props;

    const _getContactsTableHead = () => {
        return (
            <thead>
                <tr>
                    <th>
                        <span className="channel-list__name">{t('common.contact')}</span>
                    </th>
                    <th className="channel-list__messenger-header">{t('common.messengers')}</th>
                    <th>{t('common.created')}</th>
                </tr>
            </thead>
        );
    }

    const _mapContactsToTableBody = () => {
        const {contacts} = props;
        return (
            <tbody>
                {contacts.map((contact, index) => {
                    return (
                        <ContactItem 
                            contact={contact}
                            key={index} />
                    )
                })}
            </tbody>
        )
    }

    const _getContactsTable = () => {
        return (
            <React.Fragment>
                {(props.contacts.length > 0) && <Table className="contact-list-table card-table">
                    {_getContactsTableHead()}
                    {_mapContactsToTableBody()}
                </Table>}
                {props.contacts.length === 0 && !props.isReceivingContacts &&
                    <div className="contact-list__no-contacts-text">
                        {t('contact.noContacts')}
                    </div>}
            </React.Fragment>
        );
    }
 
    return (
        <div>
            <TagSearchingInput
                searchingTags={getContactSearchingTags()}
                onSearch={_onSearchClicked}
                repeatingTags={false}
                currentTags={props.mailingItemInfo.searchingTagsForContacts ?? []}
                onClearFilter={() => _onSearchClicked([])}
                contacts={props.contacts}
            />
            <div 
                ref={contactsListRef}
                onScroll={_onScroll} 
                className={`contacts-list__body ${(props.isChatVisible ? "splittered" : "")}`}>
                {_getContactsTable()}
                {(isReceivingContacts && <Loader />)}
            </div>
        </div>
    );
}

const mapStateToProps = (state: IApplicationState) => {
    return {
        mailingItemInfo: mailingItemInfoSelector(state),
        contacts: contactsSelector(state),
        isReceivingContacts: isReceivingContactsSelector(state),
        countOfContacts: countOfContactsSelector(state),
        isChatVisible: isChatVisibleSelector(state)
    };
}

const mapDispatchToProps = (dispatch: any): IMailingRecipientsContactListProps => {
    return {
        getContactsByTagsForMailing: (
            skip: number, 
            count: number,
            searchingTags: ISearchingTag[]
        ) => dispatch(getContactsByTagsForMailing(skip, count, searchingTags)),
        getContactsByTagsForMailingWithoutUpdateTags: (
            skip: number,
            count: number,
            searchingTags: ISearchingTag[],
        ) => dispatch(getContactsByTagsForMailingWithoutUpdateTags(skip, count, searchingTags))
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(MailingRecipientsContactList);